import { PluginStateManager } from "./PlugtinStateManager";
import { ClassContainer } from "./DataModelComponents/ClassContainer";
import { ClassHeader } from "./DataModelComponents/ClassHeader";
import { DiagramEventEmitter } from "../../../diagram/modules/DiagramEventEmitter";

import { FabricRectangular } from "../../../views/grid/components/FabricRectangular";
import { DiagramUtils } from "../../../diagram/diagram-assets/DiagramUtils";
import { getInstanceIDFromCanvasInstance } from "../../../diagram/diagram-assets/diagram-positioning-state";

/**
 * @description @FabricDiagram plugin - Used in DataModel UI
 */
export class DataModelClass extends PluginStateManager {
    name = "ModelClass";

    defaultSize;

    gridConfig;

    instances = {
        header: null,
        container: null
    };

    headerHeight = 30;

    eventEmitter;

    uuid;

    preview;

    expandingTo;

    constructor(options, ctx, data, state) {
        super(options, ctx, data, state);
        this.defaultSize = {
            width: 200,
            height: 200
        };
        this.eventEmitter = new DiagramEventEmitter(getInstanceIDFromCanvasInstance(ctx));
        if (data && data.uuid) this.uuid = data.uuid;
        else this.uuid = new DiagramUtils().generateUUID();

        this.instances.container = new ClassContainer(
            this.getContainerLayout(),
            this.state,
            this.ctx,
            { ...this.data, ...{ uuid: this.uuid } }
        );
        this.instances.header = new ClassHeader(
            this.getHeaderLayout(),
            this.state,
            this.ctx,
            { ...this.data, ...{ uuid: this.uuid } }
        );

        this.instances.container.setEventEmitter(this.eventEmitter);
        this.instances.header.setEventEmitter(this.eventEmitter);
    }

    /**
     * @description Saves a reference to the cell that this plugin instance is currently being expanded to
     * @param {GridCell} cell - the cell its expanding to
     */
    expandingToCell(cell) {
        const needsRedrawing = this.expandingTo !== cell;
        this.expandingTo = cell;
        if (needsRedrawing) this.draw();
    }

    /**
     * @description checks to see if the provided coordonates are inside the plugin area
     * @param { Object } mousePos - coordonates
     */
    isPointInsidePlugin(mousePos) {
        const containerPoints = { ...this.getContainerInstance().points };
        const isInContainer = this.getContainerInstance().pointInRect(mousePos, containerPoints);
        if (isInContainer) return true;
        const headerPoints = { ...this.getHeaderInstance().points };
        const isInHeader = this.getHeaderInstance().pointInRect(mousePos, headerPoints);
        if (isInHeader) return true;
        return false;
    }

    /**
     * @description draws the resizing overlay.
     */
    drawResizingOverlay() {
        if (!this.expandingTo) {
            return;
        }
        const expandingCellLayout = this.expandingTo.layout;
        const position = super.getPosition();
        const size = super.getSize();
        const expandingSize = {
            width: (expandingCellLayout.x + 100) - (position.x + size.width),
            height: (expandingCellLayout.y + 100) - (position.y + size.height)
        };
        if (expandingSize.width < 0 || expandingSize.height < 0) {
            // TODO: Must get a reference to the GridConfig file. If the cells ever get configurable, it will cause issues.
            // Currently is hardcoded 100x100 pixels for a cell
            let toClearCols; let toClearRows; let toClearHeight; let
                toClearWidth;
            if (expandingSize.height < 0) {
                // checking to see if the user is trying to resize the class to less then the mandatory minimum
                // if yes, set the expand size to the mandatory minimum
                if ((expandingSize.height * -1) > this.getSize().height - this.defaultSize.height) {
                    expandingSize.height = (this.getSize().height - this.defaultSize.height) * -1;
                }
                toClearRows = Math.floor((expandingSize.height * -1) / 100);
                toClearHeight = toClearRows * 100;
            }
            if (expandingSize.width < 0) {
                // checking to see if the user is trying to resize the class to less then the mandatory minimum
                // if yes, set the expand size to the mandatory minimum
                if ((expandingSize.width * -1) > this.getSize().width - this.defaultSize.width) {
                    expandingSize.width = (this.getSize().width - this.defaultSize.width) * -1;
                }
                toClearCols = Math.floor((expandingSize.width * -1) / 100);
                toClearWidth = toClearCols * 100;
            }
            if (!toClearHeight) toClearHeight = this.getSize().height;
            if (!toClearWidth) toClearWidth = this.getSize().width;
            // will begin to parse the points from the most outer top left corner
            const path = [];
            const startX = (this.getPosition().x + this.getSize().width) - toClearWidth;
            const startY = (this.getPosition().y + this.getSize().height) - toClearHeight;
            // checking to see if is a diagonal resize
            if (expandingSize.width < 0 && expandingSize.height < 0) {
                const classTopLeftX = this.getPosition().x;
                const classTopLeftY = this.getPosition().y;
                const classHeight = this.getSize().height;
                // start point
                path.push({ x: classTopLeftX, y: startY });
                // right point
                path.push({ x: startX, y: startY });
                // top left point
                path.push({ x: startX, y: classTopLeftY });
                // top right point
                path.push({ x: startX + toClearWidth, y: classTopLeftY });
                // bottom right point
                path.push({ x: startX + toClearWidth, y: classTopLeftY + classHeight });
                // bottom left point
                path.push({ x: classTopLeftX, y: classTopLeftY + classHeight });
                // start point to close the loop
                path.push({ x: classTopLeftX, y: startY });
            } else {
                // start point
                path.push({ x: startX, y: startY });
                // right point
                path.push({ x: startX + toClearWidth, y: startY });
                // bottom right point
                path.push({ x: startX + toClearWidth, y: startY + toClearHeight });
                // bottom left point
                path.push({ x: startX, y: startY + toClearHeight });
                // back to start point
                path.push({ x: startX, y: startY });
            }
            this.drawResizingOverlayFromPath(path, { fillStyle: "rgba(217, 0, 0, 0.1)", strokeStyle: "rgb(255, 0, 0)" });
        }
    }

    /**
     * @description Draws a resizing overlay
     * @param {Array} path - Array of paths that need
     * @param {Object} colors - { fillStyle, strokeStyle }
     */
    drawResizingOverlayFromPath(path, colors) {
        this.ctx.beginPath();
        this.ctx.setLineDash([6, 6]);
        path.forEach((pathSegment, index) => {
            if (!index) this.ctx.moveTo(pathSegment.x, pathSegment.y);
            else this.ctx.lineTo(pathSegment.x, pathSegment.y);
        });
        this.ctx.fillStyle = colors.fillStyle;
        this.ctx.fill();
        this.ctx.strokeStyle = colors.strokeStyle;
        this.ctx.stroke();
        this.ctx.setLineDash([]);
        this.ctx.closePath();
    }

    /**
     * @description Clears the  expandingTo property
     */
    clearExpanding() {
        this.expandingTo = null;
    }

    /**
     * @description Draws the plugin
     */
    draw() {
        if (this.state.drawed) {
            this.clear();
        }
        this.state.drawed = true;
        const container = this.getContainerInstance();
        const header = this.getHeaderInstance();
        container.preview = header.preview = this.preview;
        container.draw();
        header.draw();
        container.getExpandableInstance().draw();
        this.drawResizingOverlay();
    }

    /**
     * @description Returns the child plugins data
     */
    getChildrenData() {
        return this.getContainerInstance().getChildrenData();
    }

    /**
     * @description gets the child with the provided uuid
     * @param {String} uuid
     */
    getChild(uuid) {
        return this.getContainerInstance().getChild(uuid);
    }

    /**
     * @description Returns the Plugin header label
     */
    getPluginData() {
        return { label: this.getHeaderInstance().data.label };
    }

    /**
     * @description clears the plugin view
     */
    clear() {
        const position = super.getPosition();
        const size = super.getSize();
        const lineWidth = 1;
        this.ctx.clearRect(
            position.x + (lineWidth * 2),
            position.y + (lineWidth * 2),
            size.width,
            size.height
        );
    }

    /**
     * @description returns the Position and size of the container child plugin
     */
    getContainerLayout() {
        const position = { ...this.position };
        const size = { ...this.size };
        const headerLayout = this.getHeaderLayout();
        position.y += headerLayout.height;
        size.height -= headerLayout.height;
        return { ...position, ...size };
    }

    /**
     * @description returns the Position and size of the header child plugin
     */
    getHeaderLayout() {
        const whatIs30Percent = (30 * super.getSize().height) / 100;
        return {
            x: super.getPosition().x,
            y: super.getPosition().y,
            width: super.getSize().width,
            height:
                whatIs30Percent > this.headerHeight
                    ? this.headerHeight
                    : whatIs30Percent
        };
    }

    /**
     * @description Checks to see if the provided  coordonates are inside the expand point of the plugin
     * @param {Object} payload - the coordonates
     */
    isExpandPoint(payload) {
        const expandableInstance = this.getContainerInstance().getExpandableInstance();
        const fabricRect = new FabricRectangular({
            ...expandableInstance.getStartPosition(),
            width: expandableInstance.getOcupiedWidth(),
            height: expandableInstance.getOcupiedHeight()
        });
        const isExpandPoint = fabricRect.pointInRect(payload); // expandableInstance.isPointInTriangle(target, points[0], points[1], points[2]);
        return isExpandPoint;
    }

    /**
     * @description Checks to see if the provided  coordonates are inside the more items blade of the plugin
     * @param {Object} payload - the coordonates
     */
    isPropMoreItems(payload) {
        const moreItemsLayout = this.getContainerInstance().getMoreItemsLayout();
        if (moreItemsLayout) {
            const startX = moreItemsLayout.x; const endX = startX + moreItemsLayout.width; const startY = moreItemsLayout.y - 10; const
                endY = moreItemsLayout.y + moreItemsLayout.height;
            if (startX <= payload.x && payload.x <= endX
                && startY <= payload.y && payload.y <= endY) {
                this.getContainerInstance().showMoreItemsReadonly(payload);
            }
        }
    }

    /**
     * @description Checks to see if the provided  coordonates are inside the menu btn point of the plugin
     * @param {Object} payload - the coordonates
     */
    isOpenMenuPoint(payload) {
        const radius = this.instances.header.circleDiameter / 2;
        const minX = this.instances.header.menuCircleX - radius;
        const maxX = this.instances.header.menuCircleX + radius;
        const minY = this.instances.header.menuCircleY - radius;
        const maxY = this.instances.header.menuCircleY + radius;

        if (
            minX < payload.x
            && payload.x < maxX
            && minY < payload.y
            && payload.y < maxY
        ) {
            return true;
        }
        return false;
    }

    /**
    * @description Checks to see if the provided  coordonates are inside the edit btn point of the plugin
    * @param {Object} payload - the coordonates
    */
    isEditPoint(payload) {
        const radius = this.instances.header.circleDiameter / 2;
        const minX = this.instances.header.layout.x + (this.instances.header.layout.width - this.instances.header.headerMenuStyle.paddingRight
                - this.instances.header.circleDiameter - this.instances.header.headerEditButton.editWidth);
        const maxX = this.instances.header.layout.x + (this.instances.header.layout.width
            - this.instances.header.headerMenuStyle.paddingRight - this.instances.header.circleDiameter);
        const minY = this.instances.header.layout.y + radius / 2;
        const maxY = this.instances.header.layout.y + this.instances.header.layout.height - radius / 2;

        if (minX < payload.x && payload.x < maxX
            && minY < payload.y && payload.y < maxY) {
            return true;
        }
        return false;
    }

    /**
       * @description Checks to see if the provided  coordonates are inside the move point of the plugin
       * @param {Object} payload - the coordonates
       */
    isMovePoint(payload) {
        const header = this.getHeaderInstance();
        return header.isMovePoint(payload);
    }

    /**
    * @description Checks to see if the provided  coordonates are inside one of the container children ( @ClassProperty )
    * @param {Object} payload - the coordonates
    */
    childrenClickCheck(payload) {
        const content = this.getContainerInstance();
        const header = this.getHeaderInstance();
        const clickedHeader = header.pointInRect(payload);
        let clickedElem = content.childrenClickCheck(payload);
        const clickedInstanceBody = content.clickedInstanceBody(payload);
        const returnVal = {};
        if (clickedElem && clickedElem.data.type === "ADD") {
            returnVal.elem = "ADD";
            returnVal.pluginUuid = this.uuid;
            returnVal.pluginId = this.data.id;
            returnVal.className = this.data.label;
        } else if (clickedElem) {
            returnVal.elem = "CHILD";
            returnVal.pluginUuid = this.uuid;
            returnVal.pluginId = this.data.id;
            returnVal.childUuid = clickedElem.uuid;
            returnVal.childId = clickedElem.data.id;
        } else {
            clickedElem = null;
        }
        if (clickedHeader || (clickedInstanceBody && !clickedElem)) {
            returnVal.elem = "CONTAINER";
            returnVal.pluginUuid = this.uuid;
            returnVal.pluginId = this.data.id;
        }
        return returnVal;
    }

    /**
     * @description Returns the container instance
     */
    getContainerInstance() {
        return this.instances.container;
    }

    /**
     * @description Returns the header instance
     */
    getHeaderInstance() {
        return this.instances.header;
    }

    /**
     * @description Updates the plugin state, layout or/and data
     */
    update(options, state, data) {
        if (options && options.position) {
            this.setPosition(options.position);
        }
        if (options && options.size) {
            this.setSize(options.size);
        }
        if (data) {
            this.data = { ...this.data, ...data };
            this.getHeaderInstance().update({}, {}, this.data);
        }
        if (options && (options.size || options.position)) { this.updateChildrenLayout(); }
        if (state) this.updateState(state);
        if (state) this.updateChildrenState();
    }

    /**
     * @description updates header container data
     */
    updateContainerData(data) {
        this.data = { ...this.data, ...data };
        this.getHeaderInstance().update({}, {}, this.data);
        this.getHeaderInstance().draw();
    }

    /**
     * @description - Sets the children inside the content container instance
     */
    setChildren() {
        this.getContainerInstance().updateChildren(this.data.children);
    }

    /**
     * @description Adds a child
     * @param {Object} state  - The state of the future child
     * @param {Object} data - Data for the child plugin
     * @param {Function} Factory - the plugin function
     * @param {String} childPluginID  - Child plugin ID
     */
    addChild(state, data, Factory, childPluginID) {
        if (!this.data.children) this.data.children = [];
        this.data.children.push(childPluginID);
        return this.getContainerInstance().addChild(
            state,
            data,
            Factory,
            childPluginID
        );
    }

    /**
     * @description - Updates the child data
     * @param {String} containerUuid - Container Uuid
     * @param {String} childUuid -
     * @param {Object} data - Object to update the child
     */
    updateChildData(containerUuid, childUuid, data) {
        this.getContainerInstance().updateChildData(
            containerUuid,
            childUuid,
            data
        );
    }

    /**
     * @description Removes the child with the provided uuid
     * @param { String } childUuid- child uuid
     */
    removeChild(childUuid) {
        this.data.children = this.data.children.filter(
            child => child.uuid !== childUuid
        );
        return this.getContainerInstance().removeChild(childUuid);
    }

    /**
     * @description Updates child data
     */
    updateChild(data) {
        let index;
        this.data.children.find((child, childIndex) => {
            if (child.uuid === data.uuid) {
                index = childIndex;
                return child;
            }
        });
        if (index) {
            this.data.children[index] = data;
            this.getContainerInstance().updateChildren(this.data.children);
        }
    }

    /**
     * @description updates children state
     */
    updateChildrenState() {
        this.getContainerInstance().update({}, this.state);
        this.getHeaderInstance().update({}, this.state);
    }

    /**
     * @description updates children layout
     */
    updateChildrenLayout() {
        const layoutHeader = this.getHeaderLayout();
        const layoutContainer = this.getContainerLayout();
        this.getContainerInstance().update(layoutContainer);
        this.getHeaderInstance().update(layoutHeader);
    }
}
